import type { ContourId } from '@/src/store/contours/contour-definitions';
import type { ImageIndices } from '@/src/store/dicom-web/current-study-store';

import { RestrictedContourInteractionViewIDs } from '@src/config';
import { onBeforeUnmount, onMounted } from 'vue';

import { useViewportActions } from '@/src/components/pixel-overlay/useViewportActions';
import { useSmartContourPaste } from '@/src/composables/useSmartContourPaste';
import { useContourContainerStore } from '@/src/store/contours/contour-container-store';
import { ViewportDrawingMode, ViewportToolMode } from '@/src/store/contours/types/tool-modes-type';
import { useViewportStateStore } from '@/src/store/contours/viewport-state-store';
import { useCurrentStudyStore } from '@/src/store/dicom-web/current-study-store';

import { useAppState } from '../store/ocg-app-state';

/**
 * Tags/elements where keystrokes should be ignored (user is typing).
 */
const IGNORED_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT']);
const LAST_KEYSTROKE_TIME = { value: 0 };
const LAST_KEYSTROKE_KEY = { value: '' };
const LAST_VIEWER_INTERACTED = { value: '' };

function isTypingTarget(event: KeyboardEvent): boolean {
  const target = event.target as HTMLElement | null;
  if (!target) return false;
  if (IGNORED_TAGS.has(target.tagName)) return true;
  if (target.isContentEditable) return true;
  return false;
}

/**
 * Centralised app-wide keyboard shortcut handler.
 */
export function useAppKeyboardShortcuts() {
  // const handleMouseDown = (event: MouseEvent) => {
  //   // Only handle when the left mouse button (LMB) is pressed with keyboard
  //   const targetKeys = ['Alt', 'c', 'C'];
  //   if (
  //     LAST_KEYSTROKE_KEY.value === '' ||
  //     !targetKeys.includes(LAST_KEYSTROKE_KEY.value)
  //   )
  //     return; // No recent keystroke, ignore
  //   const isLMB = event.button === 0;

  //   const appStateStore = useAppState();
  //   const viewportStateStore = useViewportStateStore();

  //   const currentViewID = appStateStore.getInteractionViewer();
  //   if (!currentViewID) return;

  //   // Threshold contour drawing thresholdTriggers + LMB:
  //   if (isLMB && LAST_KEYSTROKE_KEY.value === 'Alt') {
  //     viewportStateStore.setDrawingMode(
  //       currentViewID,
  //       ViewportDrawingMode.Threshold
  //     );
  //   }

  //   if (isLMB && LAST_KEYSTROKE_KEY.value === 'c') {
  //     viewportStateStore.setDrawingMode(
  //       currentViewID,
  //       ViewportDrawingMode.PointClick
  //     );
  //   }
  // };
  const handleKeyDown = (event: KeyboardEvent) => {
    const appStateStore = useAppState();
    const viewportStateStore = useViewportStateStore();
    const currentStudyStore = useCurrentStudyStore();
    const viewportActions = useViewportActions();

    const currentViewID = appStateStore.getInteractionViewer();
    const { activeViewers } = appStateStore;
    // const { activeViewers, currentView: currentModule } = appStateStore;

    let currentImageIndices: ImageIndices | null = null;
    let selectedContour: ContourId | null = null;
    if (currentViewID) {
      currentImageIndices
        = viewportStateStore.getSelectedImageIndices(currentViewID);
      selectedContour = viewportStateStore.getSelectedContour(currentViewID);
      LAST_VIEWER_INTERACTED.value = currentViewID;
    }

    // Skip when the user is typing in an input / textarea / contenteditable
    if (isTypingTarget(event)) return;

    // Build a human-readable combo string for logging
    const parts: string[] = [];
    if (event.ctrlKey) parts.push('Ctrl');
    if (event.metaKey) parts.push('Meta/⌘');
    if (event.altKey) parts.push('Alt');
    if (event.shiftKey) parts.push('Shift');
    parts.push(event.key);
    LAST_KEYSTROKE_KEY.value = event.key;
    LAST_KEYSTROKE_TIME.value = Date.now();

    const viewerActionsAvailable
      = currentViewID && activeViewers.includes(currentViewID);
    const isShiftPressed = event.shiftKey;
    const isModifierPressed = event.metaKey || event.ctrlKey;
    const isAltPressed = event.altKey;
    const isSpacePressed = event.code === 'Space';

    if (isSpacePressed || isAltPressed) {
      event.preventDefault();
    }

    // Threshold contour drawing thresholdTriggers (Alt key alone)
    if (isAltPressed && !isModifierPressed && viewerActionsAvailable) {
      event.preventDefault();
      viewportStateStore.setDrawingMode(
        currentViewID,
        ViewportDrawingMode.Threshold,
      );
      return;
    }

    const combo = parts.join(' + ');

    // APP NAVIGATION:
    const appNavigationKeys = ['m', 'M', 'q', 'Q'];
    if (appNavigationKeys.includes(event.key)) {
      event.stopPropagation();
      event.preventDefault();

      switch (event.key) {
        case 'm':
        case 'M':
          void appStateStore.setView('ViewerComponent');
          break;
        case 'q':
        case 'Q':
          void appStateStore.setView('QualityCheck');
          break;
        default:
          break;
      }
      return;
    }

    // VIEWER NAVIGATION:
    const viewerNavigationKeys = [
      'ArrowUp',
      'ArrowDown',
      'ArrowLeft',
      'ArrowRight',
    ];
    if (viewerNavigationKeys.includes(event.key)) {
      let newIndices: ImageIndices | null = null;
      if (!viewerActionsAvailable || currentImageIndices === null) {
        console.log(
          `[Shortcut] ${combo} pressed, but no active viewer or image selected.`,
        );
        return;
      }
      event.stopPropagation();
      event.preventDefault();
      switch (event.key) {
        case 'ArrowUp': {
          if (isShiftPressed) {
            const nextCompatible = viewportStateStore.getNextCompatibleSeries(
              currentViewID,
              currentImageIndices.seriesIdx,
              currentImageIndices.seriesIdx + 1,
            );

            if (nextCompatible !== null) {
              newIndices = {
                seriesIdx: nextCompatible,
                sliceIdx: 0,
                imageIdx: 0,
              };
            }

            break;
          }
          newIndices
            = currentStudyStore.getPreviousSliceIndex(currentImageIndices);
          break;
        }
        case 'ArrowDown': {
          if (isShiftPressed) {
            const nextCompatible = viewportStateStore.getNextCompatibleSeries(
              currentViewID,
              currentImageIndices.seriesIdx,
              currentImageIndices.seriesIdx - 1,
            );

            if (nextCompatible !== null) {
              newIndices = {
                seriesIdx: nextCompatible,
                sliceIdx: 0,
                imageIdx: 0,
              };
            }
            break;
          }
          newIndices = currentStudyStore.getNextSliceIndex(currentImageIndices);
          break;
        }
        case 'ArrowLeft': {
          const tempIndices
            = currentStudyStore.getPreviousImageIndex(currentImageIndices);
          newIndices = viewportStateStore.getCompatibleImageIndex(
            currentViewID,
            tempIndices,
            currentImageIndices,
          );
          break;
        }
        case 'ArrowRight': {
          const tempIndices
            = currentStudyStore.getNextImageIndex(currentImageIndices);
          newIndices = viewportStateStore.getCompatibleImageIndex(
            currentViewID,
            tempIndices,
            currentImageIndices,
          );
          break;
        }
        default:
          break;
      }
      if (newIndices) {
        viewportStateStore.setSelectedImageIndices(currentViewID, newIndices);
      }
    }

    // Contour Handling:
    const contourKeys = [
      'c',
      'C',
      'v',
      'V',
      'a',
      'A',
      'o',
      'O',
      'p',
      'P',
      'f',
      'F',
      'l',
      'L',
      'z',
      'Z',
      'y',
      'Y',
      'r',
      'R',
      's',
      'S',
      'd',
      'D',
      'Delete',
      'Backspace',
      'Tab',
      '+',
      '=',
      '-',
      '_',
      ']',
      '[',
    ];
    const isContourKey
      = contourKeys.includes(event.key)
        || [
          'KeyC',
          'KeyV',
          'KeyA',
          'NumpadAdd',
          'NumpadSubtract',
          'Equal',
          'Minus',
          'BracketRight',
          'BracketLeft',
        ].includes(event.code);

    if (isContourKey) {
      if (!viewerActionsAvailable || currentImageIndices === null) {
        console.log(
          `[Shortcut] ${combo} pressed, but no active viewer or image selected.`,
        );
        return;
      }
      if (RestrictedContourInteractionViewIDs.includes(currentViewID)) {
        const _currentViewID: string = currentViewID; // tv: this fixes an awkward eslint complaint, possibly
        console.log(`[Shortcut] ${combo} pressed, but contour interactions are disabled in this view (${_currentViewID}).`);
        return;
      }
      event.stopPropagation();
      event.preventDefault();

      const curImageID = currentStudyStore.getImageUID(currentImageIndices);
      const contourContainerStore = useContourContainerStore();

      const { seriesIdx, sliceIdx } = currentImageIndices;
      // const { seriesIdx, imageIdx, sliceIdx } = currentImageIndices;

      switch (event.key) {
        // case 'Tab': {
        //   const currentDrawingContours =
        //     contourContainerStore.getDrawingContours(curImageID);
        //   if (
        //     !currentDrawingContours ||
        //     Object.keys(currentDrawingContours).length === 0
        //   )
        //     return;

        //   // Build a flat, ordered list of only user-editable contours
        //   const editableContours: ContourId[] = [];
        //   // eslint-disable-next-line no-restricted-syntax
        //   for (const [, ids] of Object.entries(currentDrawingContours)) {
        //     // eslint-disable-next-line no-restricted-syntax
        //     for (const [, contour] of Object.entries(ids)) {
        //       const props = contour.getProps();
        //       if (props.userEditable !== false) {
        //         editableContours.push({ label: props.label, id: props.id });
        //       }
        //     }
        //   }

        //   if (editableContours.length === 0) return;

        //   // Find the index of the currently selected contour in the editable list
        //   let currentIndex = -1;
        //   if (
        //     selectedContour !== null &&
        //     (selectedContour.label !== null || selectedContour.id !== null)
        //   ) {
        //     currentIndex = editableContours.findIndex(
        //       (c) =>
        //         c.label === selectedContour!.label &&
        //         c.id === selectedContour!.id
        //     );
        //   }

        //   // Pick the next editable contour, wrapping around to the start
        //   const nextIndex = (currentIndex + 1) % editableContours.length;
        //   const next = editableContours[nextIndex];

        //   viewportStateStore.setSelectedContour(
        //     currentViewID,
        //     next.label,
        //     next.id
        //   );
        //   viewportStateStore.setDrawingContour(currentViewID, next.label);
        //   viewportStateStore.setToolMode(
        //     currentViewID,
        //     ViewportToolMode.Contour
        //   );
        //   break;
        // }
        case 'Backspace':
        case 'Delete':
          if (isModifierPressed) {
            viewportActions.handleDeleteAll(curImageID);
          }
          else {
            viewportActions.handleDelete(currentViewID);
          }
          break;
        case 'd':
        case 'D':
          // Delete selected contour across the slice
          if (isModifierPressed && selectedContour) {
            const imageUIDs = currentStudyStore.getSliceImageUIDs(
              seriesIdx,
              sliceIdx,
            );
            if (!imageUIDs) return;
            viewportActions.handleDeleteAcrossImages(
              imageUIDs,
              selectedContour.label,
            );
          }
          break;

        case 'o':
        case 'O':
          if (isModifierPressed) {
            // Delete selected contour on image
            if (!selectedContour) return;
            viewportActions.handleDelete(currentViewID);
          }
          break;
        case 'p':
        case 'P':
          if (isModifierPressed) {
            // Delete all contours on image
            viewportActions.handleDeleteAll(curImageID);
          }
          break;
        case 'f':
        case 'F':
          if (isModifierPressed) {
            // Forward selected contour across the slice
            if (!selectedContour) return;
            const imageUIDs = currentStudyStore.getSliceImageUIDs(
              seriesIdx,
              sliceIdx,
            );
            if (!imageUIDs) return;
            viewportActions.forwardContoursAcrossUIDs(
              curImageID,
              imageUIDs,
              selectedContour.label,
            );
          }
          break;
        case 'z':
        case 'Z':
          if (isModifierPressed) {
            viewportActions.handleUndo();
          }
          break;
        case 'y':
        case 'Y':
          if (isModifierPressed) {
            viewportActions.handleRedo();
          }
          break;
        case 'r':
        case 'R':
          if (isShiftPressed && !event.repeat) {
            const cur = viewportStateStore.getToolMode(currentViewID);
            viewportStateStore.setToolMode(
              currentViewID,
              cur === ViewportToolMode.Rotate
                ? ViewportToolMode.Pan
                : ViewportToolMode.Rotate,
            );
          }
          break;
        case 's':
        case 'S':
          if (isModifierPressed) {
            const selectedContourProps
              = viewportStateStore.getSelectedContour(currentViewID);
            if (!selectedContourProps) {
              return;
            }
            contourContainerStore.smoothContour(
              curImageID,
              selectedContourProps,
              'movingAverage',
              9,
            );
          }
          break;
        case 'l':
        case 'L':
          if (isModifierPressed) {
            const selectedContourProps
              = viewportStateStore.getSelectedContour(currentViewID);
            if (!selectedContourProps) {
              return;
            }
            contourContainerStore.roundContour(
              curImageID,
              selectedContourProps,
            );
          }
          break;
        case '+':
        case '=':
        case ']':
          if (isModifierPressed) {
            const selectedContourProps
              = viewportStateStore.getSelectedContour(currentViewID);
            if (!selectedContourProps || !selectedContourProps.label) {
              return;
            }
            contourContainerStore.growContour(
              curImageID,
              selectedContourProps,
              5,
            );
            contourContainerStore.smoothContour(
              curImageID,
              selectedContourProps,
              'movingAverage',
              9,
            );
          }
          break;
        case '-':
        case '_':
        case '[':
          if (isModifierPressed) {
            const selectedContourProps
              = viewportStateStore.getSelectedContour(currentViewID);
            if (!selectedContourProps || !selectedContourProps.label) {
              return;
            }
            contourContainerStore.shrinkContour(
              curImageID,
              selectedContourProps,
              5,
            );
            contourContainerStore.smoothContour(
              curImageID,
              selectedContourProps,
              'movingAverage',
              9,
            );
          }
          break;
        case 'c':
        case 'C':
          if (isModifierPressed && !isShiftPressed && !isAltPressed) {
            // Ctrl+C: copy selected contour only. If no contour is selected, do nothing.
            if (selectedContour && selectedContour.label) {
              viewportActions.copyImageDrawingContours(
                curImageID,
                selectedContour.label,
                selectedContour.id,
              );
            }
            break;
          }
          if (isModifierPressed || isShiftPressed || isAltPressed) {
            break;
          }
          viewportStateStore.setDrawingMode(
            currentViewID,
            ViewportDrawingMode.PointClick,
          );
          break;

        case 'v':
        case 'V':
          if (!isModifierPressed) break;
          // Ctrl+Shift+Alt+V (or Ctrl+Shift+A+V): Smart paste across all intermediate phases
          if (isShiftPressed && isAltPressed) {
            void useSmartContourPaste(currentViewID, curImageID, { allPhases: true });
          }
          // Ctrl+Shift+V: Smart paste target phase only
          else if (isShiftPressed) {
            void useSmartContourPaste(currentViewID, curImageID, { allPhases: false });
          }
          // Ctrl+V: Simple paste from clipboard
          else {
            viewportActions.pasteContours(curImageID);
          }
          break;

        case 'a':
        case 'A':
          // Ctrl+Shift+A (or Ctrl+Shift+A+V shorthand): Smart paste across all intermediate phases
          if (isModifierPressed && isShiftPressed) {
            void useSmartContourPaste(currentViewID, curImageID, { allPhases: true });
          }
          break;
        default:
          break;
      }
    }
  };

  const addDelay = async (delay = 100) => {
    return new Promise((resolve) => {
      setTimeout(resolve, delay);
    });
  };

  const handleKeyUp = (event: KeyboardEvent) => {
    void (async () => {
      await addDelay();
      // event: KeyboardEvent
      LAST_KEYSTROKE_KEY.value = '';
      LAST_KEYSTROKE_TIME.value = 0;

      const isAltReleased = event.altKey || event.key === 'Alt';

      if (LAST_VIEWER_INTERACTED.value) {
        if (isAltReleased) {
          const viewportStateStore = useViewportStateStore();
          viewportStateStore.setDrawingMode(
            LAST_VIEWER_INTERACTED.value,
            ViewportDrawingMode.Freehand,
          );
        }
      }
    })();
  };

  const handleWindowBlur = () => {
    // Reset drawing mode if Alt was held (threshold mode)
    if (LAST_VIEWER_INTERACTED.value && LAST_KEYSTROKE_KEY.value === 'Alt') {
      const viewportStateStore = useViewportStateStore();
      viewportStateStore.setDrawingMode(
        LAST_VIEWER_INTERACTED.value,
        ViewportDrawingMode.Freehand,
      );
    }
    // Reset keystroke tracking so keys don't appear "stuck"
    LAST_KEYSTROKE_KEY.value = '';
    LAST_KEYSTROKE_TIME.value = 0;
    LAST_VIEWER_INTERACTED.value = '';
  };

  const handleVisibilityChange = () => {
    if (document.hidden) {
      handleWindowBlur();
    }
  };

  onMounted(() => {
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('blur', handleWindowBlur);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    // Add LMB mouse down listeners
    // window.addEventListener('mousedown', handleMouseDown);
  });

  onBeforeUnmount(() => {
    window.removeEventListener('keydown', handleKeyDown);
    window.removeEventListener('keyup', handleKeyUp);
    window.removeEventListener('blur', handleWindowBlur);
    document.removeEventListener('visibilitychange', handleVisibilityChange);

    // window.removeEventListener('mousedown', handleMouseDown);
  });
}
